import {AxiosResponse} from 'axios';
import compactObject from 'utils/libs/compact-object';
import {CompositionDetailExtended} from 'utils/types/compositions';
import {
  ActivitiesComparison,
  CompositionReviewedStatus,
  ReviewGoal,
} from 'utils/types/newReviewed';
import letrusAPI from 'APIs/letrusAPI/axiosInstance';

export interface WriteCompositionServiceData {
  id?: number | string;
  title: string;
  composition_raw: string;
  composition_source: string;
  step_number?: number | null;
}

export const fetchCompositionByIdService = (
  compositionId: number | string,
): Promise<AxiosResponse<CompositionDetailExtended>> =>
  letrusAPI(`/api/v1/compositions/${compositionId}/`);

export const writeCompositionService = (
  composition: WriteCompositionServiceData,
): any =>
  letrusAPI(`/api/v1/write_composition/`, {
    method: 'patch',
    data: {
      id: composition.id,
      title: composition.title,
      composition_raw: composition.composition_raw,
      composition_source: composition.composition_source,
      step_number: composition.step_number,
    },
  });

export const createCompositionAPIV1Service = (
  composition,
): Promise<AxiosResponse<CompositionDetailExtended>> =>
  letrusAPI('/api/v1/compositions/', {
    method: 'post',
    data: composition,
  });

export const fetchCompositionJsonStatsAsyncService = (composition, options) => {
  async function runFetch() {
    let res;
    try {
      const {data} = await letrusAPI('/api/v1/json_stats_async/', {
        method: 'post',
        data: compactObject({
          id: composition.id,
          process_comments: options.processComments ? 1 : null,
          persist_to_db: options.persistToDb ? 1 : null,
          markers_to_process: options.markersToProcess,
          composition_raw: options.compositionRaw,
        }),
      });

      res = data;
    } catch (err) {
      return err;
    }

    return res;
  }

  return runFetch();
};

export const fetchLastCompositionReviewService = (
  compositionId: string | number,
) =>
  letrusAPI(`/api/v1/compositions/${compositionId}/last_composition_review/`);

export const OCRCompositionService = (
  id: string | undefined,
  images: string[],
) =>
  letrusAPI('/api/v1/extract_composition_from_image/', {
    method: 'patch',
    data: {
      id,
      images,
    },
  });

export const addLikeInCompositionReviewService = (
  compositionReviewId: string | number,
  likeInfo: boolean,
) =>
  letrusAPI(`/api/v1/composition_reviews/${compositionReviewId}/like_info/`, {
    method: 'patch',
    data: {like_info: likeInfo},
  });

export const addLikeInCompositionReviewCompetenceService = (
  compositionReviewId: string | number,
  reviewCompetenceGradeId: string | number,
  likeInfo: boolean,
) =>
  letrusAPI(
    `/api/v1/composition_reviews/${compositionReviewId}/review_competence_grades/${reviewCompetenceGradeId}/like_info/`,
    {
      method: 'patch',
      data: {like_info: likeInfo},
    },
  );
export const fetchCompositionMarkersService = (
  compositionId: number | string,
) => letrusAPI(`/api/v1/markers/${compositionId}/`);

export const fetchCompositionReviewStatusService = (
  compositionId?: number | string,
): Promise<AxiosResponse<CompositionReviewedStatus>> =>
  letrusAPI(`/api/v1/compositions/${compositionId}/composition_review_status/`);

export const setReviewGoalService = ({
  data,
  reviewId,
}: {
  reviewId: number;
  data: {competence_id: number};
}): Promise<AxiosResponse<null>> =>
  letrusAPI(`/api/v1/composition_reviews/${reviewId}/goal_competence/`, {
    method: 'patch',
    data,
  });

export const fetchReviewGoalService = ({
  reviewId,
  competenceId,
}: {
  competenceId?: number;
  reviewId?: number;
}): Promise<AxiosResponse<ReviewGoal>> =>
  letrusAPI(
    `/api/v1/composition_reviews/${reviewId}/get_last_review_competence_goal/${competenceId}/`,
  );

export const fetchReviewComparisonActivitiesService = ({
  reviewParentId,
  reviewId,
}: {
  reviewParentId: number;
  reviewId: number;
}): Promise<AxiosResponse<ActivitiesComparison>> =>
  letrusAPI(
    `/api/v1/student_report/compare_activities/${reviewParentId}/${reviewId}/`,
  );
